import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { toast } from "sonner";
import { FileSpreadsheet, History, Crown } from "lucide-react";
import { rupees } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import CeoReports from "@/components/CeoReports";

export default function Reports() {
  const { user } = useAuth();
  const isMasterAdmin = user?.role === "master_admin";
  const [tab, setTab] = useState("franchise");
  const [franchises, setFranchises] = useState([]);
  const [distributors, setDistributors] = useState([]);
  // Franchise/Distributor report selector: composite value 'franchise:<id>' | 'distributor:<id>'
  const [selected, setSelected] = useState("");
  // Memo report source filter: composite or '__all__'
  const [memoFranchise, setMemoFranchise] = useState("__all__");
  const [memoDateFrom, setMemoDateFrom] = useState("");
  const [memoDateTo, setMemoDateTo] = useState("");
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/franchises").then((r) => setFranchises(r.data));
    api.get("/distributors").then((r) => setDistributors(r.data)).catch(() => {});
    api.get("/rates/history").then((r) => setHistory(r.data));
  }, []);

  const download = (path, filename) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api${path}`;
    const tok = localStorage.getItem("access_token");
    fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
      .then((r) => r.blob()).then((b) => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = u; a.download = filename; a.click();
        URL.revokeObjectURL(u); toast.success("Downloaded");
      });
  };

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Exports</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Reports</h1>
        <p className="text-slate-500 mt-2">Download franchise statements, master catalogues, and audit history.</p>
      </div>

      <div className="inline-flex p-1 bg-white border border-[#0D1B2A]/10 rounded-md flex-wrap">
        <button onClick={() => setTab("franchise")} className={`px-5 py-2 text-sm rounded-md ${tab === "franchise" ? "bg-[#0D1B2A] text-white" : "text-slate-600"}`} data-testid="tab-franchise">Franchise / Distributor</button>
        <button onClick={() => setTab("master")} className={`px-5 py-2 text-sm rounded-md ${tab === "master" ? "bg-[#0D1B2A] text-white" : "text-slate-600"}`} data-testid="tab-master">Master catalogue</button>
        <button onClick={() => setTab("memo")} className={`px-5 py-2 text-sm rounded-md ${tab === "memo" ? "bg-[#0D1B2A] text-white" : "text-slate-600"}`} data-testid="tab-memo">Memo report</button>
        <button onClick={() => setTab("history")} className={`px-5 py-2 text-sm rounded-md ${tab === "history" ? "bg-[#0D1B2A] text-white" : "text-slate-600"}`} data-testid="tab-history">Rate history</button>
        {isMasterAdmin && (
          <button onClick={() => setTab("ceo")} className={`px-5 py-2 text-sm rounded-md inline-flex items-center gap-1.5 ${tab === "ceo" ? "bg-[#0D1B2A] text-white" : "text-slate-600"}`} data-testid="tab-ceo">
            <Crown className="w-3.5 h-3.5" strokeWidth={1.5} /> CEO Reports
          </button>
        )}
      </div>

      {tab === "ceo" && isMasterAdmin && <CeoReports franchises={franchises} distributors={distributors} />}

      {tab === "franchise" && (
        <div className="luxe-card p-8 max-w-2xl space-y-4">
          <label className="text-xs uppercase tracking-widest text-slate-500 block">Select franchise or distributor</label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="report-franchise"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              {franchises.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Franchises</SelectLabel>
                  {franchises.map((f) => <SelectItem key={`f-${f.id}`} value={`franchise:${f.id}`}>{f.name}</SelectItem>)}
                </SelectGroup>
              )}
              {distributors.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Distributors</SelectLabel>
                  {distributors.map((d) => <SelectItem key={`d-${d.id}`} value={`distributor:${d.id}`}>{d.name}</SelectItem>)}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
          <Button disabled={!selected} onClick={() => {
            const [kind, id] = selected.split(":");
            if (kind === "franchise") {
              const f = franchises.find((x) => x.id === id);
              download(`/reports/franchise/${id}/excel`, `${f.name.replace(/\s/g, "_")}_report.xlsx`);
            } else {
              const d = distributors.find((x) => x.id === id);
              const date = new Date().toISOString().slice(0, 10);
              download(`/reports/distributor/${id}/excel`, `Distributor_Report_${d.name.replace(/\s/g, "_")}_${date}.xlsx`);
            }
          }} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="export-franchise-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Excel
          </Button>
        </div>
      )}

      {tab === "master" && (
        <div className="luxe-card p-8 max-w-2xl">
          <p className="text-sm text-slate-500 mb-4">Full catalogue across all sources with company, franchise & customer pricing.</p>
          <Button onClick={() => download(`/reports/master/excel`, `master_catalogue.xlsx`)} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="export-master-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Download master catalogue
          </Button>
        </div>
      )}

      {tab === "memo" && (
        <div className="luxe-card p-8 max-w-2xl space-y-4" data-testid="memo-report-pane">
          <p className="text-sm text-slate-500">Export memo (consignment) goods for a franchise or all franchises, optionally filtered by the memo-marked date range.</p>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Source</label>
            <Select value={memoFranchise} onValueChange={setMemoFranchise}>
              <SelectTrigger className="h-11 bg-white border-[#0D1B2A]/15" data-testid="memo-report-franchise"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All sources</SelectItem>
                {franchises.length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Franchises</SelectLabel>
                    {franchises.map((f) => <SelectItem key={`f-${f.id}`} value={f.name}>{f.name}</SelectItem>)}
                  </SelectGroup>
                )}
                {distributors.length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-400">Distributors</SelectLabel>
                    {distributors.map((d) => <SelectItem key={`d-${d.id}`} value={d.name}>{d.name}</SelectItem>)}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">From (memo marked)</label>
              <input type="date" value={memoDateFrom} onChange={(e) => setMemoDateFrom(e.target.value)} data-testid="memo-date-from"
                className="h-11 w-full px-3 rounded-md border border-[#0D1B2A]/15 bg-white" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">To</label>
              <input type="date" value={memoDateTo} onChange={(e) => setMemoDateTo(e.target.value)} data-testid="memo-date-to"
                className="h-11 w-full px-3 rounded-md border border-[#0D1B2A]/15 bg-white" />
            </div>
          </div>
          <Button onClick={() => {
            const qs = new URLSearchParams();
            if (memoFranchise && memoFranchise !== "__all__") qs.set("source_tag", memoFranchise);
            if (memoDateFrom) qs.set("date_from", memoDateFrom);
            if (memoDateTo) qs.set("date_to", memoDateTo);
            const tag = memoFranchise && memoFranchise !== "__all__" ? memoFranchise.replace(/\s/g, "_") : "all";
            download(`/reports/memo/excel?${qs.toString()}`, `memo_report_${tag}.xlsx`);
          }} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="export-memo-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Memo Report
          </Button>
        </div>
      )}

      {tab === "history" && (
        <div className="luxe-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-[#C9A84C]" />
            <h3 className="font-serif text-xl text-[#0D1B2A]">Rate change history</h3>
          </div>
          <div className="divide-y divide-[#0D1B2A]/5">
            {history.length === 0 && <div className="py-6 text-sm text-slate-400">No changes yet.</div>}
            {history.map((h, i) => (
              <div key={h.version ?? `hist-${i}`} className="py-3 flex flex-wrap justify-between gap-2 text-sm">
                <div>
                  <span className="text-[#0D1B2A] font-medium">Version {h.version}</span>
                  <span className="text-slate-400 ml-3">{h.saved_by}</span>
                </div>
                <div className="text-slate-500 text-xs">
                  Gold {rupees(h.snapshot?.metal?.gold_24kt)}/g · {new Date(h.saved_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
